'use client'

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import {
  hasCapacitorBridge,
  isNativeAppRuntime,
  markNativeAppSession,
  shouldWaitForNativeAppBridge,
} from '@/lib/native-app-runtime'
import { useReferralCaptureOnMount } from '@/lib/use-referral-capture'
import { useWebViewRecoveryReport } from '@/lib/use-webview-recovery-report'

interface CapacitorBridgeContextValue {
  isReady: boolean
  isCapacitor: boolean
  isNativeApp: boolean
}

const CapacitorBridgeContext = createContext<CapacitorBridgeContextValue>({
  isReady: false,
  isCapacitor: false,
  isNativeApp: false,
})

export function useCapacitorBridge() {
  return useContext(CapacitorBridgeContext)
}

interface CapacitorBridgeProviderProps {
  children: ReactNode
}

/**
 * Estado inicial computado de forma síncrona (sin efectos) para que el SSR y el
 * primer render en web emitan los children directamente:
 * - Servidor: no hay window → se asume web y se renderiza todo (SEO: el HTML
 *   servido debe contener el contenido; antes el gate devolvía null y TODO el
 *   sitio llegaba vacío a los crawlers).
 * - Web (navegador normal): la detección es síncrona → listo desde el primer
 *   render, coincide con el HTML del servidor (hidratación limpia).
 * - WebView nativo de Capacitor: hay que esperar al bridge (se inyecta async).
 *   El primer render cliente devuelve null: React descarta el HTML del servidor
 *   (mismatch esperado, cubierto por la splash screen) y re-renderiza al estar
 *   el bridge listo. En ese entorno el SEO es irrelevante.
 */
function computeInitialState(): CapacitorBridgeContextValue {
  if (typeof window === 'undefined') {
    return { isReady: true, isCapacitor: false, isNativeApp: false }
  }
  if (!shouldWaitForNativeAppBridge()) {
    return { isReady: true, isCapacitor: false, isNativeApp: isNativeAppRuntime() }
  }
  if (hasCapacitorBridge()) {
    return { isReady: true, isCapacitor: true, isNativeApp: true }
  }
  return { isReady: false, isCapacitor: false, isNativeApp: false }
}

/**
 * CapacitorBridgeProvider waits for the Capacitor JavaScript bridge to be ready
 * before rendering children. This prevents race conditions where the app tries
 * to use Capacitor APIs before the native bridge has been injected.
 *
 * When loading a remote URL in Capacitor, the bridge is injected asynchronously.
 * Without this provider, code that checks for window.Capacitor on mount may
 * find it undefined, causing "JS Eval error" messages in Xcode.
 *
 * The wait (render null) only applies inside the native WebView; on the web and
 * during SSR children render immediately so crawlers receive the full HTML.
 */
export function CapacitorBridgeProvider({ children }: CapacitorBridgeProviderProps) {
  const [state, setState] = useState<CapacitorBridgeContextValue>(computeInitialState)

  // AC2.1 — captura del `?ref` de la landing (web), detrás del master-flag público.
  // Extraído a un hook para no engordar la complejidad de este componente.
  useReferralCaptureOnMount()

  // Salud de la app nativa: reporta si el arranque anterior murió con el render
  // process del WebView (android/…/RenderProcessGoneListener.java).
  useWebViewRecoveryReport(state.isCapacitor)

  useEffect(() => {
    if (state.isReady) {
      // markNativeAppSession es un side effect: no puede vivir en el
      // inicializador de useState (debe ser puro), se aplica aquí.
      if (state.isCapacitor) markNativeAppSession()
      return
    }

    // In Capacitor environment, wait for the bridge
    const checkBridge = () => {
      if (hasCapacitorBridge()) {
        markNativeAppSession()
        setState({ isReady: true, isCapacitor: true, isNativeApp: true })
        return true
      }
      return false
    }

    // Check immediately in case bridge is already available
    if (checkBridge()) return

    // Poll for bridge availability (Capacitor injects it async)
    let attempts = 0
    const maxAttempts = 50 // 5 seconds max wait
    const interval = setInterval(() => {
      attempts++
      if (checkBridge() || attempts >= maxAttempts) {
        clearInterval(interval)
        // If we hit max attempts, proceed anyway but log warning
        if (attempts >= maxAttempts) {
          console.warn('[CapacitorBridgeProvider] Bridge not detected after 5s, proceeding as web')
          setState({ isReady: true, isCapacitor: false, isNativeApp: isNativeAppRuntime() })
        }
      }
    }, 100)

    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al montar: el estado inicial decide si hay que esperar al bridge
  }, [])

  // Solo en el WebView nativo: no renderizar hasta que el bridge esté listo
  // (la splash screen sigue visible durante la espera).
  if (!state.isReady) {
    return null
  }

  return (
    <CapacitorBridgeContext.Provider value={state}>
      {children}
    </CapacitorBridgeContext.Provider>
  )
}
